import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ICandidateReview } from "../Typings/Candidate";
import {
  ICandidateReviewResponse,
  ISaveReviewCommand,
  ISaveReviewHelpfulCommand,
} from "../Typings/CandidateProfile";
import { apiPathCandidateProfile, fetcher, secureFetcher } from "./queryHelpers";
import { useAuthToken } from "../State/Hooks/authHooks";

/**
 * Save or update a candidate review
 *
 * Called using [variable].mutate(arg: ISaveReviewCommand)
 *
 * @returns UseMutationResult<boolean, unknown, ISaveReviewCommand, unknown>
 */
export const useSaveCandidateReviewMutation = () => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  return useMutation<boolean, unknown, ISaveReviewCommand, ICandidateReviewResponse | undefined>(
    (saveReviewCommand) =>
      fetcher(apiPathCandidateProfile + "review/save", {
        method: "POST",
        body: JSON.stringify(saveReviewCommand),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }),
    {
      onMutate: (saveReviewCommand) => optimisticReviewUpdate(saveReviewCommand, queryClient),
      onSettled: (response, error: any, saveReviewCommand, originalCandidateProfile) => {
        if ((error || response === false) && originalCandidateProfile) {
          queryClient.setQueryData(
            ["candidateReviews", saveReviewCommand.candidateId],
            originalCandidateProfile,
          );
        } else {
          // Return the invalidateQueries promise here to keep this mutation
          // in a loading state until the refetch is complete
          return queryClient.invalidateQueries(["candidateReviews", saveReviewCommand.candidateId]);
        }
      },
    },
  );
};

const optimisticReviewUpdate = async (
  saveReviewCommand: ISaveReviewCommand,
  queryClient: QueryClient,
): Promise<ICandidateReviewResponse | undefined> => {
  const { candidateId, id: reviewId, ...reviewDetails } = saveReviewCommand;
  const reviewDate = new Date();

  await queryClient.cancelQueries(["candidateReviews", candidateId]);

  const originalCandidateProfile = queryClient.getQueryData<ICandidateReviewResponse>([
    "candidateReviews",
    candidateId,
  ]);

  if (originalCandidateProfile) {
    queryClient.setQueryData<ICandidateReviewResponse>(["candidateReviews", candidateId], {
      ...originalCandidateProfile,
      reviews: reviewId
        ? originalCandidateProfile.reviews.map((review: ICandidateReview) => {
            if (review.id === reviewId) {
              return {
                ...review,
                ...reviewDetails,
              };
            }
            return review;
          })
        : [
            ...originalCandidateProfile.reviews,
            {
              ...reviewDetails,
              id: "",
              lastUpdatedDate: reviewDate,
              reviewDate,
              reviewerName: "You",
              reviewerNickname: "You",
              reviewerUsername: "You",
              votes: [],
            },
          ],
    });
  }

  return originalCandidateProfile;
};

/**
 * Save a vote on whether a review was helpful or not
 *
 * Called using [variable].mutate(arg: IHelpfulnessBody)
 *
 * @returns UseMutationResult<boolean, unknown, IHelpfulnessBody, unknown>
 */
export const useSaveReviewHelpfulnessMutation = () => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  return useMutation<boolean, unknown, ISaveReviewHelpfulCommand, ICandidateReviewResponse | undefined>(
    (saveReviewHelpfulCommand) =>
      fetcher(apiPathCandidateProfile + "review/vote", {
        method: "POST",
        body: JSON.stringify(saveReviewHelpfulCommand),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }),
    {
      onMutate: (saveReviewHelpfulCommand) =>
        optimisticHelpfulnessUpdate(saveReviewHelpfulCommand, queryClient),
      onSettled: (response, error: any, saveReviewHelpfulCommand, originalCandidateProfile) => {
        if ((error || response === false) && originalCandidateProfile) {
          queryClient.setQueryData(
            ["candidateReviews", saveReviewHelpfulCommand.candidateId],
            originalCandidateProfile,
          );
        } else {
          // Return the invalidateQueries promise here to keep this mutation
          // in a loading state until the refetch is complete
          return queryClient.invalidateQueries(["candidateReviews", saveReviewHelpfulCommand.candidateId]);
        }
      },
    },
  );
};

const optimisticHelpfulnessUpdate = async (
  saveReviewHelpfulCommand: ISaveReviewHelpfulCommand,
  queryClient: QueryClient,
): Promise<ICandidateReviewResponse | undefined> => {
  const { candidateId, reviewId, voteId, rating } = saveReviewHelpfulCommand;

  await queryClient.cancelQueries(["candidateReviews", candidateId]);

  const originalCandidateProfile = queryClient.getQueryData<ICandidateReviewResponse>([
    "candidateReviews",
    candidateId,
  ]);

  if (originalCandidateProfile) {
    queryClient.setQueryData<ICandidateReviewResponse>(["candidateReviews", candidateId], {
      ...originalCandidateProfile,
      reviews: originalCandidateProfile.reviews.map((review) =>
        review.id === reviewId
          ? {
              ...review,
              votes: review.votes.map((vote) => (vote.id === voteId ? { ...vote, rating } : vote)),
            }
          : review,
      ),
    });
  }
  return originalCandidateProfile;
};

export function useCandidateReviewsQuery(candidateId: string) {
  const token = useAuthToken();
  return useQuery<ICandidateReviewResponse>(
    ["candidateReviews", candidateId],
    () => secureFetcher(apiPathCandidateProfile + candidateId + "/review", token),
    {
      enabled: !!candidateId && !!token,
    },
  );
}
